import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getStreamers } from "../api/apis";
import { getFromLocalStorage, removeFromLocalStorage } from "../utils/localStorage";
import { Search, Users, Zap, ExternalLink, Youtube, Instagram } from "lucide-react";
import ProtectedRoute from "../Components/ProtectedRoute";

const Streamers = () => {
  const navigate = useNavigate();
  const [streamers, setStreamers] = useState([]);
  const [filteredStreamers, setFilteredStreamers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const fetchedRef = useRef(false);
  const abortControllerRef = useRef(null);

  const token = getFromLocalStorage("token");
  const user = getFromLocalStorage("user");

  const checkTimeout = useCallback(() => {
    const expirationTime = getFromLocalStorage("expirationTime");
    if (!expirationTime || Date.now() > parseInt(expirationTime, 10)) {
      removeFromLocalStorage("token");
      removeFromLocalStorage("adminToken");
      removeFromLocalStorage("user");
      removeFromLocalStorage("expirationTime");
      return false;
    }
    return true;
  }, []);

  const handleLogout = useCallback(() => {
    removeFromLocalStorage("token");
    removeFromLocalStorage("adminToken");
    removeFromLocalStorage("user");
    navigate("/");
    window.location.reload();
  }, [navigate]);

  const fetchStreamers = useCallback(async () => {
    if (fetchedRef.current) return;

    if (!checkTimeout()) {
      handleLogout();
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const token = getFromLocalStorage("token");
      const user = getFromLocalStorage("user");

      if (!token) {
        if (!window.__suppressAuthChecks) {
          toast.error("No token found. Please log in again.");
          navigate("/login");
        }
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user
        },
        signal: controller.signal
      };

      const response = await getStreamers(config);

      console.log('📡 Streamers API Response:', response);

      if (!controller.signal.aborted) {
        if (response?.data?.status === "success") {
          console.log('✅ Streamers fetched successfully:', response.data.streamers);

          // Show only active streamers with 'pro' membership plan
          const activeStreamers = response.data.streamers?.filter(streamer =>
            streamer.isActive !== false && (streamer.membershipPlan === 'pro' || streamer.isPro === true)
          ) || [];

          console.log(`📊 Active streamers count: ${activeStreamers.length}`);
          setStreamers(activeStreamers);
          setFilteredStreamers(activeStreamers);
        } else {
          console.error('❌ Streamers fetch failed:', response?.data);
          setStreamers([]);
          setFilteredStreamers([]);
          toast.error(response?.data?.error || "Failed to fetch streamers.");
        }
        fetchedRef.current = true;
      }
    } catch (error) {
      if (error.name !== 'AbortError' && !controller.signal.aborted) {
        console.error('Error fetching streamers:', error);
        if (error.response?.status === 401) {
          handleLogout();
        } else if (!error.message?.includes('canceled')) {
          toast.error('Failed to load streamers. Please try again.');
        }
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [navigate, checkTimeout, handleLogout]);

  useEffect(() => {
    if (fetchedRef.current) return;

    const timeoutId = setTimeout(() => {
      fetchStreamers();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchStreamers]);

  // Filter streamers based on search
  useEffect(() => {
    const filtered = streamers.filter((streamer) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        streamer.username?.toLowerCase().includes(searchLower) ||
        streamer.emailId?.toLowerCase().includes(searchLower) ||
        streamer.description?.toLowerCase().includes(searchLower)
      );
    });
    setFilteredStreamers(filtered);
  }, [searchQuery, streamers]);

  const handleShowCards = (streamer) => {
    const username = streamer?.username || 'unknown';
    const emailId = streamer?.emailId;
    navigate(`/streamers/${encodeURIComponent(username)}/cards`, { state: { streamerEmailId: emailId, username } });
  };

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <main className="pt-6 pb-12 px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                    Streamers
                  </h1>
                  <p className="text-gray-400">Discover content creators and their cards</p>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="bg-black/30 rounded-xl p-6 mb-8 border border-gray-800 backdrop-blur-sm">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by username or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors"
                />
              </div>
            </div>

            {/* Streamers Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {/* Loading Skeletons */}
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div
                    key={i}
                    className="bg-black/30 rounded-xl p-6 border border-gray-700/50 backdrop-blur-sm animate-pulse"
                  >
                    {/* Avatar Skeleton */}
                    <div className="flex justify-center mb-4">
                      <div className="w-20 h-20 rounded-full bg-gray-700/50"></div>
                    </div>
                    {/* Info Skeleton */}
                    <div className="text-center space-y-3">
                      <div className="h-6 bg-gray-700/50 rounded w-3/4 mx-auto"></div>
                      <div className="h-4 bg-gray-700/50 rounded w-full"></div>
                      <div className="h-4 bg-gray-700/50 rounded w-2/3 mx-auto"></div>
                      <div className="h-10 bg-gray-700/50 rounded mt-4"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredStreamers.length === 0 ? (
                  <div className="col-span-full text-center py-20">
                    <div className="text-gray-400 text-lg">
                      {searchQuery ? "No streamers found matching your search." : "No streamers found."}
                    </div>
                  </div>
                ) : (
                  filteredStreamers.map((streamer) => (
                    <div
                      key={streamer.emailId || streamer.id || streamer._id || Math.random().toString(36).substring(2, 9)}
                      className="bg-black/30 rounded-xl p-6 border border-gray-700/50 backdrop-blur-sm hover:border-yellow-500/50 transition-all duration-300 hover:transform hover:scale-105"
                    >
                      {/* Avatar */}
                      <div className="flex justify-center mb-4">
                        <div className="relative">
                          {(streamer.profilePhoto || streamer.profilePicture) ? (
                            <img
                              src={streamer.profilePhoto || streamer.profilePicture}
                              alt={streamer.username}
                              className="w-20 h-20 rounded-full border-2 border-yellow-500/30 object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={`w-20 h-20 rounded-full border-2 border-yellow-500/30 bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-2xl ${(streamer.profilePhoto || streamer.profilePicture) ? 'hidden' : 'flex'}`}
                            style={{ display: (streamer.profilePhoto || streamer.profilePicture) ? 'none' : 'flex' }}
                          >
                            {streamer.username ? streamer.username.charAt(0).toUpperCase() : 'U'}
                          </div>

                        </div>
                      </div>

                      {/* Streamer Info */}
                      <div className="text-center space-y-3">
                        <h3 className="text-xl font-bold text-white">
                          {streamer.username}
                        </h3>
                        <p className="text-gray-400 text-sm line-clamp-2">
                          {streamer.description || "No description available"}
                        </p>

                        <div className="pt-2 flex flex-col space-y-3">
                          {/* Show Cards Button */}
                          <button
                            onClick={() => handleShowCards(streamer)}
                            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-2 px-4 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200 flex items-center justify-center space-x-2"
                          >
                            <Zap className="w-4 h-4" />
                            <span>Show Cards</span>
                          </button>

                          {/* Social Media Links */}
                          {(streamer.youtubeLink || streamer.instagramLink) && (
                            <div className="flex justify-center space-x-4 pt-2">
                              {streamer.youtubeLink && (
                                <a
                                  href={streamer.youtubeLink.startsWith('http') ? streamer.youtubeLink : `https://youtube.com/${streamer.youtubeLink}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-red-500 hover:text-red-400 transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Youtube className="w-5 h-5" />
                                </a>
                              )}
                              {streamer.instagramLink && (
                                <a
                                  href={streamer.instagramLink.startsWith('http') ? streamer.instagramLink : `https://instagram.com/${streamer.instagramLink.replace('@', '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-pink-500 hover:text-pink-400 transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Instagram className="w-5 h-5" />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </ProtectedRoute>
  );
};

export default Streamers;
